import { useEffect, useState } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { ShoppingBasket, Menu, X, User, LogOut, Sprout, Shield, Package } from 'lucide-react';
import { Logo } from '@/components/brand/logo';
import { Button } from '@/components/ui/button';
import { useCart } from '@/features/cart/cart-store';
import { useAuth } from '@/features/auth/auth-context';
import { cn } from '@/lib/utils';
import { transitions } from '@/components/motion/primitives';

const NAV = [
  { to: '/shop', label: 'Shop' },
  { to: '/farms', label: 'Our farms' },
  { to: '/about', label: 'Why direct' },
] as const;

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);

  const navigate = useNavigate();
  const reduced = useReducedMotion();
  const cartCount = useCart((state) => state.lines.length);
  const { isAuthenticated, isFarmer, isAdmin, user, signOut } = useAuth();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  // Solid background once scrolled: the hero sits under a transparent header,
  // but over white page content transparent nav is unreadable.
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close both menus on navigation — otherwise the mobile drawer stays open
  // over the page you just navigated to.
  //
  // Adjusted during render rather than in an effect. This is React's
  // documented pattern for "reset state when a prop changes": it re-renders
  // before the browser paints, so the menu never flashes open on the new
  // page. An effect would set state after paint and cascade a second render.
  const [lastPathname, setLastPathname] = useState(pathname);
  if (pathname !== lastPathname) {
    setLastPathname(pathname);
    setIsMenuOpen(false);
    setIsAccountOpen(false);
  }

  // Lock body scroll behind the drawer.
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  async function handleSignOut() {
    await signOut();
    void navigate({ to: '/' });
  }

  return (
    <>
      <motion.header
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={transitions.base}
        className={cn(
          'fixed inset-x-0 top-0 z-50 transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
          isScrolled
            ? 'border-b border-soil-200/70 bg-soil-50/85 backdrop-blur-xl shadow-soft'
            : 'border-b border-transparent bg-transparent',
        )}
      >
        <div className="mx-auto flex h-18 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <Link to="/" className="shrink-0" aria-label="SreeRasa home">
            <Logo size="sm" />
          </Link>

          {/* ── Desktop nav ── */}
          <nav className="hidden items-center gap-1 md:flex" aria-label="Main">
            {NAV.map((item) => {
              const isActive = pathname.startsWith(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    'relative rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                    isActive ? 'text-farm-700' : 'text-soil-600 hover:text-soil-900',
                  )}
                >
                  {item.label}
                  {/* layoutId makes the underline slide between items rather
                      than cross-fading — the single nicest four lines in the
                      whole header. */}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute inset-x-3 -bottom-px h-0.5 rounded-full bg-farm-600"
                      transition={transitions.spring}
                    />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* ── Actions ── */}
          <div className="flex items-center gap-1.5">
            <Link
              to="/cart"
              className="relative flex size-10 items-center justify-center rounded-xl text-soil-700 transition-colors hover:bg-soil-100 hover:text-farm-700"
              aria-label={cartCount > 0 ? `Cart, ${cartCount} items` : 'Cart, empty'}
            >
              <ShoppingBasket className="size-5" aria-hidden />
              <AnimatePresence>
                {cartCount > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    transition={transitions.spring}
                    className="absolute -right-0.5 -top-0.5 flex size-5 items-center justify-center rounded-full bg-sun-500 text-[10px] font-bold text-white ring-2 ring-soil-50"
                  >
                    {cartCount}
                  </motion.span>
                )}
              </AnimatePresence>
            </Link>

            {isAuthenticated ? (
              <div className="relative hidden md:block">
                <button
                  type="button"
                  onClick={() => setIsAccountOpen((open) => !open)}
                  className="flex size-10 items-center justify-center rounded-xl text-soil-700 transition-colors hover:bg-soil-100"
                  aria-label="Account menu"
                  aria-expanded={isAccountOpen}
                  aria-haspopup="menu"
                >
                  <User className="size-5" aria-hidden />
                </button>

                <AnimatePresence>
                  {isAccountOpen && (
                    <>
                      {/* Click-away catcher. Cheaper and more reliable than a
                          document listener that has to ignore the trigger. */}
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setIsAccountOpen(false)}
                        aria-hidden
                      />
                      <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.96 }}
                        transition={transitions.quick}
                        role="menu"
                        className="absolute right-0 top-12 z-20 w-56 overflow-hidden rounded-xl border border-soil-200 bg-white p-1.5 shadow-float"
                      >
                        <p className="truncate px-3 py-2 text-xs text-soil-500">{user?.email}</p>
                        <div className="my-1 h-px bg-soil-100" />

                        <MenuLink
                          to="/orders"
                          icon={<Package className="size-4" />}
                          label="My orders"
                        />
                        {isFarmer && (
                          <MenuLink
                            to="/farmer"
                            icon={<Sprout className="size-4" />}
                            label="Farmer dashboard"
                          />
                        )}
                        {isAdmin && (
                          <MenuLink
                            to="/admin"
                            icon={<Shield className="size-4" />}
                            label="Admin console"
                          />
                        )}

                        <div className="my-1 h-px bg-soil-100" />
                        <button
                          type="button"
                          onClick={handleSignOut}
                          role="menuitem"
                          className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-soil-700 transition-colors hover:bg-soil-50"
                        >
                          <LogOut className="size-4" aria-hidden />
                          Sign out
                        </button>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Button
                size="sm"
                className="hidden md:inline-flex"
                onClick={() => navigate({ to: '/auth/sign-in' })}
              >
                Sign in
              </Button>
            )}

            <button
              type="button"
              onClick={() => setIsMenuOpen(true)}
              className="flex size-10 items-center justify-center rounded-xl text-soil-700 transition-colors hover:bg-soil-100 md:hidden"
              aria-label="Open menu"
            >
              <Menu className="size-5" aria-hidden />
            </button>
          </div>
        </div>
      </motion.header>

      {/* ── Mobile drawer ── */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={transitions.quick}
              onClick={() => setIsMenuOpen(false)}
              className="fixed inset-0 z-60 bg-soil-950/40 backdrop-blur-sm md:hidden"
              aria-hidden
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={reduced ? { duration: 0 } : transitions.softSpring}
              className="fixed inset-y-0 right-0 z-70 w-[min(20rem,85vw)] bg-white p-5 shadow-float md:hidden"
              role="dialog"
              aria-modal="true"
              aria-label="Menu"
            >
              <div className="mb-6 flex items-center justify-between">
                <Logo size="sm" />
                <button
                  type="button"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex size-10 items-center justify-center rounded-xl text-soil-600 hover:bg-soil-100"
                  aria-label="Close menu"
                >
                  <X className="size-5" aria-hidden />
                </button>
              </div>

              <nav className="space-y-1" aria-label="Mobile">
                {NAV.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="block rounded-xl px-4 py-3 font-medium text-soil-800 transition-colors hover:bg-farm-50 hover:text-farm-700"
                  >
                    {item.label}
                  </Link>
                ))}

                <div className="my-3 h-px bg-soil-100" />

                {isAuthenticated ? (
                  <>
                    <Link
                      to="/orders"
                      className="block rounded-xl px-4 py-3 font-medium text-soil-800 hover:bg-farm-50"
                    >
                      My orders
                    </Link>
                    {isFarmer && (
                      <Link
                        to="/farmer"
                        className="block rounded-xl px-4 py-3 font-medium text-soil-800 hover:bg-farm-50"
                      >
                        Farmer dashboard
                      </Link>
                    )}
                    {isAdmin && (
                      <Link
                        to="/admin"
                        className="block rounded-xl px-4 py-3 font-medium text-soil-800 hover:bg-farm-50"
                      >
                        Admin console
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={handleSignOut}
                      className="block w-full rounded-xl px-4 py-3 text-left font-medium text-danger hover:bg-red-50"
                    >
                      Sign out
                    </button>
                  </>
                ) : (
                  <div className="space-y-2 px-1 pt-2">
                    <Button full onClick={() => navigate({ to: '/auth/sign-in' })}>
                      Sign in
                    </Button>
                    <Button
                      full
                      variant="outline"
                      onClick={() => navigate({ to: '/auth/sign-up' })}
                    >
                      Create an account
                    </Button>
                  </div>
                )}
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

function MenuLink({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) {
  return (
    <Link
      to={to}
      role="menuitem"
      className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-soil-700 transition-colors hover:bg-soil-50"
    >
      {icon}
      {label}
    </Link>
  );
}
